import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle2, AlertCircle, Lightbulb, FileText, PenLine, ScrollText } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { api, errMsg } from "../lib/api";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

const GRADE_TONE = {
  "A*": "bg-primary/15 text-primary",
  "A": "bg-primary/10 text-primary",
  "B": "bg-emerald-500/10 text-emerald-500",
  "C": "bg-amber-500/10 text-amber-500",
  "D": "bg-orange-500/10 text-orange-500",
  "E": "bg-orange-500/10 text-orange-500",
  "U": "bg-destructive/10 text-destructive",
};

export default function SubmissionReview() {
  const { id } = useParams();
  const [submission, setSubmission] = useState(null);
  const [paper, setPaper] = useState(null);
  const [paperMissing, setPaperMissing] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoadError(null);
    setPaperMissing(false);
    try {
      const { data: sub } = await api.get(`/examiner/submissions/${id}`);
      setSubmission(sub);
      if (sub.paper_id) {
        try {
          const { data: p } = await api.get(`/papers/${sub.paper_id}`);
          setPaper(p);
        } catch (_) {
          setPaperMissing(true);
        }
      } else {
        setPaperMissing(true);
      }
    } catch (e) {
      setLoadError(errMsg(e));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  if (loadError) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full rounded-3xl border border-border bg-card p-8 text-center" data-testid="submission-review-error">
          <p className="text-sm font-semibold text-destructive mb-2">Couldn't load this submission</p>
          <p className="text-xs text-muted-foreground mb-6">{loadError}</p>
          <Button data-testid="submission-review-retry-btn" onClick={load} className="rounded-full glow-btn" size="sm">Retry</Button>
        </div>
      </div>
    );
  }

  if (!submission) {
    return <div className="min-h-[60vh] flex items-center justify-center" data-testid="submission-review-loading"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const questionByNumber = {};
  (paper?.questions || []).forEach((q) => { if (q?.number != null) questionByNumber[Number(q.number)] = q; });
  const markSchemeByNumber = {};
  (paper?.mark_scheme || []).forEach((m) => { if (m?.number != null) markSchemeByNumber[Number(m.number)] = m; });

  const grade = submission.estimated_grade || "-";
  const gradeTone = GRADE_TONE[grade] || "bg-muted text-foreground";

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="submission-review-page">
      <div className="mb-6">
        <Link to="/my-papers?tab=completed" data-testid="submission-review-back" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="h-3 w-3" /> Back to My Papers
        </Link>
      </div>

      {/* Header */}
      <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          {formatDate(submission.created_at)}{submission.paper_number ? ` · Paper ${submission.paper_number}` : ""}{submission.subject ? ` · ${submission.subject}` : ""}
        </p>
        <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight leading-tight">
          {paper?.title || "Marked paper"}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Badge className={`px-3 py-1 text-xs font-bold ${gradeTone}`} data-testid="submission-grade">{grade}</Badge>
          <span className="text-sm font-semibold" data-testid="submission-score">{submission.total_awarded}/{submission.total_available} marks · {submission.percentage}%</span>
          {submission.time_taken_minutes != null && (
            <span className="text-xs text-muted-foreground">{submission.time_taken_minutes} min</span>
          )}
        </div>
        {submission.overall_feedback && (
          <div className="mt-6 rounded-2xl bg-primary/5 border border-primary/20 p-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-primary mb-1.5">Overall feedback</p>
            <p className="text-sm leading-relaxed text-foreground/90">{submission.overall_feedback}</p>
          </div>
        )}
        {paperMissing && (
          <p className="mt-4 text-xs text-muted-foreground italic">The original paper is no longer available, so question stems and mark schemes below may be missing. Your answers and the examiner's feedback are still shown.</p>
        )}
      </div>

      {/* Per-question review */}
      <ol className="mt-6 space-y-4">
        {(submission.results || []).map((r, i) => {
          const q = questionByNumber[Number(r.number)];
          const ms = markSchemeByNumber[Number(r.number)];
          const hasAnswer = typeof r.student_answer === "string" && r.student_answer.trim().length > 0;
          return (
            <li key={r.number ?? i} data-testid={`review-question-${r.number}`}
                className="rounded-3xl border border-border bg-card p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="rounded-full bg-muted px-2 py-0.5 font-semibold">Q{r.number}</span>
                  {q?.command_word && <span>{q.command_word}</span>}
                  {q?.topic && <span className="truncate max-w-[220px]">· {q.topic}</span>}
                </div>
                <Badge className="bg-primary/15 text-primary hover:bg-primary/15" data-testid={`review-marks-${r.number}`}>
                  {r.marks_awarded}/{r.marks_available} marks
                </Badge>
              </div>

              {/* Question stem */}
              <div className="mt-4">
                <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-2"><FileText className="h-3 w-3" /> Question</p>
                {q?.text ? (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid={`review-stem-${r.number}`}>{q.text}</p>
                ) : (
                  <p className="text-sm italic text-muted-foreground">Question stem not available for this attempt.</p>
                )}
              </div>

              {/* Student's answer */}
              <div className="mt-5">
                <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-2"><PenLine className="h-3 w-3" /> Your answer</p>
                {hasAnswer ? (
                  <div className="rounded-2xl bg-muted/30 border border-border/60 p-4 text-sm leading-relaxed whitespace-pre-wrap" data-testid={`review-answer-${r.number}`}>
                    {r.student_answer}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-border/60 p-4 text-sm italic text-muted-foreground" data-testid={`review-answer-empty-${r.number}`}>
                    Answer not recorded for this attempt.
                  </div>
                )}
              </div>

              {/* Mark scheme / model comparison */}
              {(ms?.scheme || r.mark_scheme_comparison) && (
                <div className="mt-5">
                  <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-2"><ScrollText className="h-3 w-3" /> Mark scheme</p>
                  {ms?.scheme && (
                    <p className="text-sm leading-relaxed text-foreground/90 mb-2">{ms.scheme}</p>
                  )}
                  {r.mark_scheme_comparison && (
                    <p className="text-xs leading-relaxed text-muted-foreground italic">{r.mark_scheme_comparison}</p>
                  )}
                </div>
              )}

              {/* Examiner feedback */}
              <div className="mt-5 space-y-3">
                {r.justification && (
                  <p className="text-sm leading-relaxed" data-testid={`review-justification-${r.number}`}>{r.justification}</p>
                )}
                {r.strengths && r.strengths.length > 0 && (
                  <div>
                    <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-500 mb-1"><CheckCircle2 className="h-3 w-3" /> Strengths</p>
                    <ul className="text-xs space-y-1.5 text-muted-foreground list-disc pl-4">
                      {r.strengths.map((s, j) => <li key={j}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {r.weaknesses && r.weaknesses.length > 0 && (
                  <div>
                    <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-amber-500 mb-1"><AlertCircle className="h-3 w-3" /> Weaknesses</p>
                    <ul className="text-xs space-y-1.5 text-muted-foreground list-disc pl-4">
                      {r.weaknesses.map((s, j) => <li key={j}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {r.improvements && r.improvements.length > 0 && (
                  <div>
                    <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-primary mb-1"><Lightbulb className="h-3 w-3" /> How to improve</p>
                    <ul className="text-xs space-y-1.5 text-muted-foreground list-disc pl-4">
                      {r.improvements.map((s, j) => <li key={j}>{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
